/** Indian phone helpers — ISD +91, 10-digit local number. */

export const DEFAULT_ISD = "+91";

/** Extract 10-digit local part from any stored phone string. */
export function parsePhoneLocal(value: string | null | undefined): string {
  let digits = (value ?? "").replace(/\D/g, "");
  // Strip +91 prefix when present — never use slice(-10) (breaks on backspace).
  if (digits.startsWith("91") && digits.length > 10) {
    digits = digits.slice(2);
  }
  return digits.slice(0, 10);
}

/** Full value for API/storage: +91XXXXXXXXXX */
export function formatPhoneForApi(local: string): string {
  return `${DEFAULT_ISD}${local}`;
}

/** Normalize any phone field value before sending to the API. */
export function toPhoneForApi(value: string): string {
  const local = parsePhoneLocal(value);
  return local ? formatPhoneForApi(local) : "";
}

const TEL_URI_PREFIX = "tel:";

/** Click-to-call / QR URI: `tel:+91XXXXXXXXXX`. Empty when the number is not 10 digits. */
export function toTelUri(value: string | null | undefined): string {
  const local = parsePhoneLocal(value);
  if (local.length !== 10) return "";
  return `${TEL_URI_PREFIX}${formatPhoneForApi(local)}`;
}

export function validateIndianMobileLocal(local: string): string | null {
  if (local.length !== 10) {
    return "Enter a 10-digit mobile number";
  }
  if (!/^[6-9]/.test(local)) {
    return "Enter a valid Indian mobile number";
  }
  return null;
}

/** Mantine form validator — required phone. */
export function validateIndianPhoneField(value: string): string | null {
  return validateIndianMobileLocal(parsePhoneLocal(value));
}

/** Mantine form validator — optional phone. */
export function validateIndianPhoneFieldOptional(value: string): string | null {
  const local = parsePhoneLocal(value);
  if (!local) return null;
  return validateIndianMobileLocal(local);
}

export function maskPhone(phone: string): string {
  const local = parsePhoneLocal(phone);
  if (local.length < 4) return "****";
  return `${DEFAULT_ISD}******${local.slice(-4)}`;
}
